import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import {
    Position,
    positionKey
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

export type BallDirection = 'down' | 'right';

export interface BallPath {
    positions: Position[];
    digitsHit: number[];
}

/**
 * Create a normalized teleporter pair key from two positions.
 * Ensures the same key regardless of order.
 */
function pairKey(a: Position, b: Position): string {
    const keyA = positionKey(a);
    const keyB = positionKey(b);
    return keyA < keyB ? `${keyA}-${keyB}` : `${keyB}-${keyA}`;
}

/**
 * Get all cells between two positions (exclusive).
 * Only works for cells in the same row or column.
 */
function getCellsBetween(a: Position, b: Position): Position[] {
    const cells: Position[] = [];

    if (a.row === b.row) {
        // Same row - horizontal line
        const minCol = Math.min(a.col, b.col);
        const maxCol = Math.max(a.col, b.col);
        for (let col = minCol + 1; col < maxCol; col++) {
            cells.push({ row: a.row, col });
        }
    } else if (a.col === b.col) {
        // Same column - vertical line
        const minRow = Math.min(a.row, b.row);
        const maxRow = Math.max(a.row, b.row);
        for (let row = minRow + 1; row < maxRow; row++) {
            cells.push({ row, col: a.col });
        }
    }

    return cells;
}

export class CrossingGame {
    protected grid: Grid;
    private pendingTeleporter: Position | null = null;
    protected teleporterPairs: Map<string, Position> = new Map(); // posKey(A) -> B
    private selectedDigit: number | null = null;

    // Cached computed state
    private nextDigitDown: Map<number, number | null> = new Map();
    private nextDigitRight: Map<number, number | null> = new Map();

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
        this.computeState();
    }

    getGrid(): Grid {
        return this.grid;
    }

    getPendingTeleporter(): Position | null {
        return this.pendingTeleporter;
    }

    getSelectedDigit(): number | null {
        return this.selectedDigit;
    }

    /**
     * Get all teleporter positions.
     */
    getTeleporters(): Position[] {
        const positions: Position[] = [];
        const seen = new Set<string>();
        for (const [key, paired] of this.teleporterPairs) {
            if (!seen.has(key)) {
                const [row, col] = key.split(',').map(Number);
                positions.push({ row, col });
                seen.add(key);
            }
            const pairedKey = positionKey(paired);
            if (!seen.has(pairedKey)) {
                positions.push(paired);
                seen.add(pairedKey);
            }
        }
        return positions;
    }

    /**
     * Get all teleporter pairs as [A, B] arrays.
     */
    getTeleporterPairs(): Array<[Position, Position]> {
        const pairs: Array<[Position, Position]> = [];
        const seen = new Set<string>();

        for (const [keyA, posB] of this.teleporterPairs) {
            const key = pairKey(
                { row: parseInt(keyA.split(',')[0]), col: parseInt(keyA.split(',')[1]) },
                posB
            );
            if (!seen.has(key)) {
                const [row, col] = keyA.split(',').map(Number);
                pairs.push([{ row, col }, posB]);
                seen.add(key);
            }
        }

        return pairs;
    }

    /**
     * Get the paired teleporter position for a given position.
     */
    getPairedTeleporter(pos: Position): Position | null {
        return this.teleporterPairs.get(positionKey(pos)) || null;
    }

    /**
     * Check if a position has a teleporter.
     */
    hasTeleporter(pos: Position): boolean {
        return this.teleporterPairs.has(positionKey(pos));
    }

    /**
     * Check if a position is valid for placing a teleporter.
     * Cannot place on digits or between existing teleporter pairs.
     */
    isValidTeleporterPosition(pos: Position): boolean {
        if (!this.grid.isValidPosition(pos)) return false;
        if (this.grid.hasDigitAt(pos)) return false;
        if (this.hasTeleporter(pos)) return false;

        // Check if position is between any existing teleporter pair
        for (const [posA, posB] of this.getTeleporterPairs()) {
            const cellsBetween = getCellsBetween(posA, posB);
            for (const cell of cellsBetween) {
                if (cell.row === pos.row && cell.col === pos.col) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Get valid positions where the second teleporter of a pair can be placed.
     * Must be in the same row or column as the first, with no digits between.
     */
    getValidPairPositions(from: Position): Position[] {
        const valid: Position[] = [];

        // Check all cells in the same row
        for (let col = 0; col < this.grid.size; col++) {
            if (col === from.col) continue;
            const pos = { row: from.row, col };
            if (this.isValidSecondTeleporter(from, pos)) {
                valid.push(pos);
            }
        }

        // Check all cells in the same column
        for (let row = 0; row < this.grid.size; row++) {
            if (row === from.row) continue;
            const pos = { row, col: from.col };
            if (this.isValidSecondTeleporter(from, pos)) {
                valid.push(pos);
            }
        }

        return valid;
    }

    /**
     * Check if pos can be the second teleporter paired with from.
     */
    private isValidSecondTeleporter(from: Position, to: Position): boolean {
        if (!this.isValidTeleporterPosition(to)) return false;

        // Must be in same row or column
        if (from.row !== to.row && from.col !== to.col) return false;

        // No digit between them
        const cellsBetween = getCellsBetween(from, to);
        for (const cell of cellsBetween) {
            if (this.grid.hasDigitAt(cell)) return false;
            // Also no teleporter between
            if (this.hasTeleporter(cell)) return false;
        }

        return true;
    }

    /**
     * Handle a click on a cell for teleporter placement.
     * Returns true if state changed.
     */
    handleCellClick(pos: Position): boolean {
        // Clicking on a digit toggles path display
        if (this.grid.hasDigitAt(pos)) {
            const digit = this.grid.getDigitAt(pos)!;
            this.selectedDigit = this.selectedDigit === digit ? null : digit;
            return true;
        }

        // Clicking on existing teleporter removes the pair
        if (this.hasTeleporter(pos)) {
            this.removeTeleporter(pos);
            this.pendingTeleporter = null;
            return true;
        }

        // If we have a pending teleporter
        if (this.pendingTeleporter) {
            // Clicking on pending cancels it
            if (pos.row === this.pendingTeleporter.row && pos.col === this.pendingTeleporter.col) {
                this.pendingTeleporter = null;
                return true;
            }

            // Clicking on valid pair position completes the pair
            if (this.isValidSecondTeleporter(this.pendingTeleporter, pos)) {
                this.addTeleporterPair(this.pendingTeleporter, pos);
                this.pendingTeleporter = null;
                return true;
            }

            // Clicking on another valid position: cancel pending and start new
            if (this.isValidTeleporterPosition(pos)) {
                this.pendingTeleporter = pos;
                return true;
            }

            // Invalid click: just cancel pending
            this.pendingTeleporter = null;
            return true;
        }

        // No pending - start new pending if valid
        if (this.isValidTeleporterPosition(pos)) {
            this.pendingTeleporter = pos;
            return true;
        }

        return false;
    }

    /**
     * Add a teleporter pair.
     */
    private addTeleporterPair(a: Position, b: Position): void {
        const keyA = positionKey(a);
        const keyB = positionKey(b);
        this.teleporterPairs.set(keyA, b);
        this.teleporterPairs.set(keyB, a);
        this.computeState();
    }

    /**
     * Cancel a pending (half-placed) teleporter.
     */
    cancelPending(): void {
        this.pendingTeleporter = null;
    }

    /**
     * Remove a teleporter pair at the given position, if one exists.
     * Returns true if a pair was removed.
     */
    removeTeleporterAt(pos: Position): boolean {
        if (!this.hasTeleporter(pos)) return false;
        this.removeTeleporter(pos);
        return true;
    }

    /**
     * Remove a teleporter and its pair.
     */
    private removeTeleporter(pos: Position): void {
        const key = positionKey(pos);
        const paired = this.teleporterPairs.get(key);
        if (paired) {
            this.teleporterPairs.delete(key);
            this.teleporterPairs.delete(positionKey(paired));
            this.computeState();
        }
    }

    /**
     * Simulate a ball's path from a starting position.
     */
    simulateBallPath(startPos: Position, direction: BallDirection): BallPath {
        const positions: Position[] = [startPos];
        const digitsHit: number[] = [];
        const startDigit = this.grid.getDigitAt(startPos);
        if (startDigit !== null) {
            digitsHit.push(startDigit);
        }

        let current = { ...startPos };
        const maxSteps = this.grid.size * this.grid.size * 2; // Prevent infinite loops

        for (let step = 0; step < maxSteps; step++) {
            // Move one step
            let next: Position;
            if (direction === 'down') {
                next = { row: (current.row + 1) % this.grid.size, col: current.col };
            } else {
                next = { row: current.row, col: (current.col + 1) % this.grid.size };
            }

            // Check for teleporter at next position
            const paired = this.teleporterPairs.get(positionKey(next));
            if (paired) {
                // Teleport: add teleporter entry, then exit
                positions.push(next);
                next = { ...paired };
            }

            positions.push(next);
            current = next;

            // Check for digit at current position
            const digit = this.grid.getDigitAt(current);
            if (digit !== null) {
                digitsHit.push(digit);
                // Stop after hitting a digit (for next-digit calculation)
                break;
            }

            // Check if we've returned to start
            if (current.row === startPos.row && current.col === startPos.col) {
                break;
            }
        }

        return { positions, digitsHit };
    }

    /**
     * Get the next digit hit by a ball starting from a given digit.
     */
    getNextDigitForBall(fromDigit: number, direction: BallDirection): number | null {
        if (direction === 'down') {
            return this.nextDigitDown.get(fromDigit) ?? null;
        } else {
            return this.nextDigitRight.get(fromDigit) ?? null;
        }
    }

    /**
     * Compute cached state (next digits for each ball).
     */
    protected computeState(): void {
        this.nextDigitDown.clear();
        this.nextDigitRight.clear();

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.grid.getDigitPosition(digit);
            if (!pos) continue;

            // Compute next digit for down ball
            const pathDown = this.simulateBallPath(pos, 'down');
            if (pathDown.digitsHit.length > 1) {
                this.nextDigitDown.set(digit, pathDown.digitsHit[1]);
            }

            // Compute next digit for right ball
            const pathRight = this.simulateBallPath(pos, 'right');
            if (pathRight.digitsHit.length > 1) {
                this.nextDigitRight.set(digit, pathRight.digitsHit[1]);
            }
        }
    }

    /**
     * Get digits where both balls have the correct next digit.
     */
    getCorrectDigits(): number[] {
        const correct: number[] = [];

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const expectedNext = this.grid.nextDigit(digit);
            const nextDown = this.nextDigitDown.get(digit);
            const nextRight = this.nextDigitRight.get(digit);

            if (nextDown === expectedNext && nextRight === expectedNext) {
                correct.push(digit);
            }
        }

        return correct;
    }

    /**
     * Check if a digit has correct next for down ball.
     */
    isCorrectDown(digit: number): boolean {
        const expectedNext = this.grid.nextDigit(digit);
        return this.nextDigitDown.get(digit) === expectedNext;
    }

    /**
     * Check if a digit has correct next for right ball.
     */
    isCorrectRight(digit: number): boolean {
        const expectedNext = this.grid.nextDigit(digit);
        return this.nextDigitRight.get(digit) === expectedNext;
    }

    /**
     * Check if the puzzle is solved.
     * Both balls must visit digits 1-9 in cyclic order from every digit.
     */
    checkWin(): boolean {
        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const expectedNext = this.grid.nextDigit(digit);

            const nextDown = this.nextDigitDown.get(digit);
            const nextRight = this.nextDigitRight.get(digit);

            if (nextDown !== expectedNext || nextRight !== expectedNext) {
                return false;
            }
        }

        return true;
    }

    /**
     * Get empty cells visited by both balls (excluding digits and teleporters).
     */
    getOverlapCells(): Position[] {
        const posStart = this.grid.getDigitPosition(this.grid.minDigit);
        if (!posStart) return [];

        const downCells = new Set<string>();
        const rightCells = new Set<string>();

        const pathDown = this.simulateFullPath(posStart, 'down');
        const pathRight = this.simulateFullPath(posStart, 'right');

        for (const pos of pathDown) {
            downCells.add(positionKey(pos));
        }
        for (const pos of pathRight) {
            rightCells.add(positionKey(pos));
        }

        const overlap: Position[] = [];
        for (const key of downCells) {
            if (rightCells.has(key)) {
                const [row, col] = key.split(',').map(Number);
                const pos = { row, col };
                if (!this.grid.hasDigitAt(pos) && !this.hasTeleporter(pos)) {
                    overlap.push(pos);
                }
            }
        }

        return overlap;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const pairCount = this.teleporterPairs.size / 2;
        const overlapCount = this.getOverlapCells().length;

        return calculateScore(pairCount, overlapCount);
    }

    /** Scoring-rules overlay: cells both balls cross (secondary). Teleporters (primary)
     *  are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        return { secondaryCells: this.getOverlapCells() };
    }

    /**
     * Simulate full ball path until returning to start.
     */
    protected simulateFullPath(startPos: Position, direction: BallDirection): Position[] {
        const positions: Position[] = [{ ...startPos }];
        let current = { ...startPos };
        const maxSteps = this.grid.size * this.grid.size * 2;

        for (let step = 0; step < maxSteps; step++) {
            let next: Position;
            if (direction === 'down') {
                next = { row: (current.row + 1) % this.grid.size, col: current.col };
            } else {
                next = { row: current.row, col: (current.col + 1) % this.grid.size };
            }

            const paired = this.teleporterPairs.get(positionKey(next));
            if (paired) {
                positions.push(next);
                next = { ...paired };
            }

            positions.push(next);
            current = next;

            if (current.row === startPos.row && current.col === startPos.col) {
                break;
            }
        }

        return positions;
    }

    isStarted(): boolean {
        return this.teleporterPairs.size > 0 || this.pendingTeleporter !== null;
    }

    reset(): void {
        this.pendingTeleporter = null;
        this.teleporterPairs.clear();
        this.selectedDigit = null;
        this.computeState();
    }

    /**
     * Jumpstart a solution: place teleporters so both balls reach digit 2 from
     * digit 1. The down ball is steered from digit 1's column to digit 2's
     * column, the right ball from digit 1's row to digit 2's row — one pair per
     * axis where possible, else a two-pair hop through an intermediate line when
     * a single pair is blocked by another digit. Candidate plans per axis are
     * combined and verified together against the ball simulator.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.grid.getDigitPosition(min);
        const d2 = this.grid.getDigitPosition(min + 1);
        if (!d1 || !d2) return false;

        const downPlans = this.findDownPlans(d1, d2);
        const rightPlans = this.findRightPlans(d1, d2);
        this.reset();
        if (downPlans.length === 0 || rightPlans.length === 0) return false;

        // Combine (one-pair plans come first, so minimal placements win), then
        // verify the full board — catches any interference between the axes.
        for (const dp of downPlans) {
            for (const rp of rightPlans) {
                this.reset();
                if (!this.placeAll([...dp, ...rp])) continue;
                if (this.isCorrectDown(min) && this.isCorrectRight(min)) {
                    this.selectedDigit = min;   // show digit 1's ball paths
                    return true;
                }
            }
        }

        this.reset();
        return false;
    }

    /**
     * Plans that make the down ball reach digit 2 from digit 1: a single
     * same-row pair (column c1 → c2), or a two-pair hop through an intermediate
     * column when no single row works. Each is verified in isolation.
     */
    private findDownPlans(d1: Position, d2: Position): Array<Array<[Position, Position]>> {
        const min = this.grid.minDigit;
        const plans: Array<Array<[Position, Position]>> = [];

        // One-pair: same-row pair carrying the down ball from column c1 to c2.
        for (const rowX of this.lineOrder(d1.row, d2.row)) {
            if (rowX === d1.row || rowX === d2.row) continue;
            const plan: Array<[Position, Position]> = [
                [{ row: rowX, col: d1.col }, { row: rowX, col: d2.col }],
            ];
            this.reset();
            if (this.placeAll(plan) && this.isCorrectDown(min)) plans.push(plan);
            if (plans.length >= 3) break;
        }

        // Two-pair hop: catch the ball in column c1's clear run, drop down an
        // intermediate column cm, deliver into column c2's clear run above d2.
        const raList = this.clearRun('col', d1.col, d1.row, 1);   // below digit 1
        const rbList = this.clearRun('col', d2.col, d2.row, -1);  // above digit 2
        let added = 0;
        for (const cm of this.lineOrder(d1.col, d2.col)) {
            if (cm === d1.col || cm === d2.col) continue;
            let foundForCm = false;
            for (const ra of raList) {
                for (const rb of rbList) {
                    if (ra === rb) continue;
                    const plan: Array<[Position, Position]> = [
                        [{ row: ra, col: d1.col }, { row: ra, col: cm }],
                        [{ row: rb, col: cm }, { row: rb, col: d2.col }],
                    ];
                    this.reset();
                    if (this.placeAll(plan) && this.isCorrectDown(min)) {
                        plans.push(plan); added++; foundForCm = true; break;
                    }
                }
                if (foundForCm) break;
            }
            if (added >= 6) break;
        }

        this.reset();
        return plans;
    }

    /** As findDownPlans, transposed: steer the right ball from row r1 to row r2. */
    private findRightPlans(d1: Position, d2: Position): Array<Array<[Position, Position]>> {
        const min = this.grid.minDigit;
        const plans: Array<Array<[Position, Position]>> = [];

        // One-pair: same-column pair carrying the right ball from row r1 to r2.
        for (const colY of this.lineOrder(d1.col, d2.col)) {
            if (colY === d1.col || colY === d2.col) continue;
            const plan: Array<[Position, Position]> = [
                [{ row: d1.row, col: colY }, { row: d2.row, col: colY }],
            ];
            this.reset();
            if (this.placeAll(plan) && this.isCorrectRight(min)) plans.push(plan);
            if (plans.length >= 3) break;
        }

        // Two-pair hop through an intermediate row rm.
        const caList = this.clearRun('row', d1.row, d1.col, 1);   // right of digit 1
        const cbList = this.clearRun('row', d2.row, d2.col, -1);  // left of digit 2
        let added = 0;
        for (const rm of this.lineOrder(d1.row, d2.row)) {
            if (rm === d1.row || rm === d2.row) continue;
            let foundForRm = false;
            for (const ca of caList) {
                for (const cb of cbList) {
                    if (ca === cb) continue;
                    const plan: Array<[Position, Position]> = [
                        [{ row: d1.row, col: ca }, { row: rm, col: ca }],
                        [{ row: rm, col: cb }, { row: d2.row, col: cb }],
                    ];
                    this.reset();
                    if (this.placeAll(plan) && this.isCorrectRight(min)) {
                        plans.push(plan); added++; foundForRm = true; break;
                    }
                }
                if (foundForRm) break;
            }
            if (added >= 6) break;
        }

        this.reset();
        return plans;
    }

    /**
     * Up to 3 indices along a row or column, stepping out from `start` until a
     * digit is hit — the clear run a ball can traverse before being stopped.
     * fixed='col' walks rows down/up column `line`; fixed='row' walks columns.
     */
    private clearRun(fixed: 'row' | 'col', line: number, start: number, step: 1 | -1): number[] {
        const size = this.grid.size;
        const out: number[] = [];
        for (let k = 1; k < size; k++) {
            const idx = (start + step * k + size * size) % size;
            const pos = fixed === 'col' ? { row: idx, col: line } : { row: line, col: idx };
            if (this.grid.hasDigitAt(pos)) break;
            out.push(idx);
            if (out.length >= 5) break;
        }
        return out;
    }

    /** Place a list of teleporter pairs; false (leaving partial state) if any is illegal. */
    private placeAll(pairs: Array<[Position, Position]>): boolean {
        for (const [a, b] of pairs) {
            if (!this.canPlaceLegalPair(a, b)) return false;
            this.addTeleporterPair(a, b);
        }
        return true;
    }

    /** Whether a fresh teleporter pair (a,b) can be legally placed right now. */
    private canPlaceLegalPair(a: Position, b: Position): boolean {
        return this.isValidTeleporterPosition(a)
            && this.isValidTeleporterPosition(b)
            && this.isValidSecondTeleporter(a, b);
    }

    /** Row/column indices ordered by closeness to either anchor (prefers tidy, near placements). */
    private lineOrder(p1: number, p2: number): number[] {
        const idx = Array.from({ length: this.grid.size }, (_, i) => i);
        idx.sort((a, b) =>
            Math.min(Math.abs(a - p1), Math.abs(a - p2)) -
            Math.min(Math.abs(b - p1), Math.abs(b - p2)));
        return idx;
    }

    /**
     * Where both balls leaving a digit end up, against where they should: the
     * status-bar reading of that digit's two badges.
     */
    private describeBallPaths(digit: number): string {
        const expected = this.grid.nextDigit(digit);
        const down = this.getNextDigitForBall(digit, 'down');
        const right = this.getNextDigitForBall(digit, 'right');

        // A ball with no other digit along its line wraps the grid and arrives back
        // where it set off; only one lost in a teleporter loop reaches nothing (the
        // "?" badge).
        const reached = (d: number | null) =>
            d === null ? 'no digit'
                : d === digit ? `digit ${digit} again`
                    : `digit ${d}`;

        if (down === expected && right === expected) {
            return `From digit ${digit}, both balls correctly reach digit ${expected}.`;
        }

        if (down === expected) {
            return `From digit ${digit}, the downward ball correctly reaches digit ${expected} ` +
                `but the rightward ball reaches ${reached(right)}.`;
        }

        if (right === expected) {
            return `From digit ${digit}, the rightward ball correctly reaches digit ${expected} ` +
                `but the downward ball reaches ${reached(down)}.`;
        }

        const target = `From digit ${digit}, balls should reach digit ${expected}.`;

        if (down === null && right === null) {
            return `${target} Neither ball reaches a digit.`;
        }

        if (down === right) {
            return down === digit
                ? `${target} Both balls come back to digit ${digit}.`
                : `${target} Both balls reach digit ${down} instead.`;
        }

        return `${target} The downward ball reaches ${reached(down)}, ` +
            `the rightward ball reaches ${reached(right)}.`;
    }

    getStatus(): string {
        if (this.checkWin()) {
            return 'You win! Both balls cross digits in order!';
        }

        if (this.pendingTeleporter) {
            return 'Click a highlighted cell to complete the teleporter pair.';
        }

        if (this.selectedDigit !== null) {
            return this.describeBallPaths(this.selectedDigit);
        }

        if (!this.isStarted()) {
            return 'Click to place paired teleporters. Balls fall down and shift right.';
        }

        return 'Click empty cells to place a teleporter pair, click digits to see paths.';
    }

    /**
     * Serialize game state to string format.
     * Format: "A0B0,C3C5" - comma-separated teleporter pairs (position pairs).
     */
    serialize(): string {
        const pairs = this.getTeleporterPairs();
        return pairs.map(([a, b]) =>
            `${this.gs.serializePosition(a)}${this.gs.serializePosition(b)}`
        ).join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 4)) {
            try {
                const posA = this.gs.deserializePosition(entry.slice(0, 2));
                const posB = this.gs.deserializePosition(entry.slice(2, 4));

                // Validate and add the pair
                if (this.grid.isValidPosition(posA) && this.grid.isValidPosition(posB)) {
                    const keyA = positionKey(posA);
                    const keyB = positionKey(posB);
                    this.teleporterPairs.set(keyA, posB);
                    this.teleporterPairs.set(keyB, posA);
                }
            } catch {
                // Invalid format, skip
            }
        }

        this.computeState();
    }
}
